import { computed, inject } from '@angular/core';
import { ApiService } from '@libs/core/base-config';
import {
  AsyncState,
  asResult,
  computedFailure,
  dataFromState,
  initialState,
  loadingState,
  successState,
} from '@libs/core/lib/state/async-states';
import {
  categoriesList,
  categoryCreate,
  deleteCategory,
  patchCategory,
} from '@libs/mtjr/shared/generated/entities/category/service/category.service';
import { Dtos, Entity } from '@libs/mtjr/shared/generated/types';
import { patchState, signalStoreFeature, withComputed, withMethods, withState } from '@ngrx/signals';

export function withCategoryDelete() {
  return signalStoreFeature(
    withState<{ categoryDeleteState: AsyncState<void>; deleteId: string }>({
      categoryDeleteState: initialState(),
      deleteId: '',
    }),
    withMethods((store, apiService = inject(ApiService)) => ({
      async categoryDelete(id: string) {
        patchState(store, { categoryDeleteState: loadingState(), deleteId: id });
        const result = await asResult(deleteCategory(apiService, id));
        patchState(store, { categoryDeleteState: result });
      },
    })),
    withComputed((store) => ({
      categoryDeleting: computed(() => {
        if (store.categoryDeleteState().type === 'loading' && store.deleteId() !== '') {
          return store.deleteId();
        }
        return null;
      }),
      categoryDeleteFailure: computedFailure(store.categoryDeleteState),
    })),
  );
}

export function withCategoryPatch() {
  return signalStoreFeature(
    withState<{ categoryPatchState: AsyncState<Dtos.UpdateResult> }>({
      categoryPatchState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async categoryPatch(id: string, category: Partial<Entity.CategoryUpdate>) {
        patchState(store, { categoryPatchState: loadingState() });
        const result = await asResult(patchCategory(apiService, id, category));
        patchState(store, { categoryPatchState: result });
      },
    })),
    withComputed((store) => ({
      categoryPatchLoading: computed(() => store.categoryPatchState().type === 'loading'),
      categoryPatchFailure: computedFailure(store.categoryPatchState),
    })),
  );
}

export function withCategoriesList() {
  type CategoriesListState = {
    categoriesListState: AsyncState<Dtos.CategoriesListResponse>;
    categoriesListInput: Dtos.CategoriesListQueryParams;
    categoriesListCanFetchNext: boolean;
    categoriesListFetchingNext: boolean;
  };
  return signalStoreFeature(
    withState<CategoriesListState>({
      categoriesListState: initialState(),
      categoriesListInput: { page: 1, totalPerPage: 20 },
      categoriesListCanFetchNext: false,
      categoriesListFetchingNext: false,
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async categoriesListPage(input?: Partial<Dtos.CategoriesListQueryParams>) {
        const mergedInput = { ...store.categoriesListInput(), ...input };
        patchState(store, {
          categoriesListState: mergedInput.page === 1 ? loadingState() : store.categoriesListState(),
          categoriesListInput: mergedInput,
          categoriesListFetchingNext: mergedInput.page > 1,
        });
        const result = await asResult(categoriesList(apiService, store.categoriesListInput()));
        if (result.success) {
          const cState = store.categoriesListState();
          let categories = result.data.categories;
          if (cState.success && mergedInput.page > 1) {
            categories = [...cState.data.categories, ...categories];
          }
          patchState(store, {
            categoriesListState: successState({ ...result.data, categories: categories }),
            categoriesListCanFetchNext: result.data.categories.length === mergedInput.totalPerPage,
            categoriesListFetchingNext: false,
          });
        } else {
          patchState(store, {
            categoriesListState: result,
            categoriesListCanFetchNext: false,
            categoriesListFetchingNext: false,
          });
        }
      },

      async categoriesListFetchNextPage() {
        if (store.categoriesListCanFetchNext() === false) return;
        const input = store.categoriesListInput();
        await this.categoriesListPage({ ...input, page: input.page + 1 });
      },
    })),

    withComputed((store) => ({
      categories: dataFromState(store.categoriesListState, (data) => data?.categories ?? []),
      categoriesFailure: computedFailure(store.categoriesListState),
    })),
  );
}

export function withCategoryCreate() {
  return signalStoreFeature(
    withState<{ categoryCreateState: AsyncState<Entity.Category> }>({
      categoryCreateState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async categoryCreate(categoryCreateBody: Dtos.CategoryCreateBody) {
        patchState(store, { categoryCreateState: loadingState() });
        const result = await asResult(categoryCreate(apiService, categoryCreateBody));
        patchState(store, { categoryCreateState: result });
      },
    })),
    withComputed((store) => ({
      categoryCreateLoading: computed(() => store.categoryCreateState().type === 'loading'),
      categoryCreateFailure: computedFailure(store.categoryCreateState),
    })),
  );
}
